import dynamic from 'next/dynamic';
import styles from './Body.module.css';
import {PDFViewer, PdfPicker} from './../pdfViewer';
import type { PdfPickerProps } from './../pdfViewer';
import {Sources} from './../Sources'
// PDFViewer is heavy (lodash, react state, canvas) — load client-side only.
// This eliminates SSR hydration mismatches and keeps the static HTML lean.

interface PdfViewerOptions {
  rootPath?: string;
  rootString?: string | null;
  initialString?: string | null;
  searchQuery?: string;
}

interface BodyProps {
  meta: {
    content?: string;
    media?: any[];
    // broken links found in content (reconcile_page_links) — drives Sources'
    // Wayback fallback + "archived" badge
    deadlinks?: { url: string; wayback?: string }[];
    // pdfs: array of baseId strings — each gets its own PDFViewer instance
    pdfs?: string[];
    // legacy single-viewer option (preserved for backwards compat)
    pdfViewer?: PdfViewerOptions | boolean;
    // one-viewer picker (cover slider + dropdown) over many PDFs
    pdfPicker?: PdfPickerProps;
  };
  // kept for legacy call sites that pass pdfViewer directly
  pdfViewer?: PdfViewerOptions | boolean;
}

const Body: React.FC<BodyProps> = ({ meta, pdfViewer: legacyProp = false }) => {
  // ---- PDF rendering decision ----
  // Priority: meta.pdfs[] (new, multi-PDF) > meta.pdfViewer > legacyProp
  const pdfIds: string[] = meta.pdfs ?? [];

  // Legacy single-viewer path — only used when pdfs[] is absent
  const legacyViewer = meta.pdfViewer ?? legacyProp;
  const legacyViewerProps =
    legacyViewer && typeof legacyViewer === 'object'
      ? {
          rootPath:      legacyViewer.rootPath      ?? 'pdfs',
          rootString:    legacyViewer.rootString     ?? null,
          initialString: legacyViewer.initialString  ?? null,
          searchQuery:   legacyViewer.searchQuery    ?? '',
        }
      : null;

  return (
    <div className={styles.body}>
      <div className={styles.container}>

        {/* ---- One-viewer PDF picker (cover slider + dropdown) ----
           Rendered above the content as the page's centerpiece when
           variables.json declares pdfPicker. Interactive, so it's a real client
           component (page content is static HTML with scripts stripped). */}
        {meta.pdfPicker?.items?.length ? <PdfPicker {...meta.pdfPicker} /> : null}

        {/* ---- Page content ----
           Legacy pages inject arbitrary (sometimes malformed) HTML via
           dangerouslySetInnerHTML. The browser re-parses/normalizes it, which
           can differ from the server string; suppressHydrationWarning tells
           React to trust the server HTML for this subtree instead of erroring. */}
        <div
          className={styles.content}
          data-page-body
          suppressHydrationWarning
          dangerouslySetInnerHTML={{ __html: meta.content || '' }}
        />

        {meta.content?.includes('youtube.com/embed') && (
          <p className={styles.note}>
            Note: YouTube videos may not load if tracking protection is enabled.
          </p>
        )}

        {/* ---- PDFs declared in variables.json pdfs[] ---- */}
        {pdfIds.length > 0 && (
          <section className={styles.pdfSection}>
            {pdfIds.map((baseId) => (
              <div key={baseId} className={styles.pdfContainer}>
                <PDFViewer
                  baseId={baseId}
                  rootPath="pdfs"
                />
              </div>
            ))}
          </section>
        )}

        {/* ---- Legacy single-viewer (pdfViewer: {...} in variables.json) ---- */}
        {pdfIds.length === 0 && legacyViewerProps && (
          <section className={styles.pdfSection}>
            <div className={styles.pdfContainer}>
              <PDFViewer {...legacyViewerProps} />
            </div>
          </section>
        )}

        {/* ---- References / Sources ---- */}
        {meta.media && <Sources media={meta.media} deadlinks={meta.deadlinks} />}

      </div>
    </div>
  );
};

export default Body;
